import { NewLessonQuestionLink } from './NewLessonQuestionLink.js';
import type { WorkItemSummary } from '../types/workflow.js';
import { workItemPatchTitle, workItemTitle } from '../lib/clusterMeta.js';
import { isLessonFill, isPlacedLesson, isStepAppend, practiceItemAttachment } from '../lib/workItems.js';
import { useEntityTitle } from '../lib/entityTitles.js';

/**
 * Row/card title for a work item. Patch titles render as-is; items whose patch carries no
 * display text (prerequisite rewires target an EXISTING entity, so their patch is only the
 * edge change) resolve the target entity's live title instead of showing a bare numeric id.
 *
 * Decoupled practice items (task-target creates carrying `intendedParent`) additionally get
 * a secondary line naming the lesson they will attach to, until the reconciler mirrors
 * `attachedEntityId` onto the work item.
 */
export function WorkItemTitle({ item, plainQuestionReference = false }: { item: WorkItemSummary; plainQuestionReference?: boolean }) {
  const newLessonQuestion = item.op === 'create' && !!item.intendedParent?.newLessonWorkItemId;
  const laneLesson = isPlacedLesson(item);
  const fallback = laneLesson || isStepAppend(item) || newLessonQuestion
    ? item.targetId ? `${item.targetType} #${item.targetId}` : `Work item (review reference ${item.workItemId})`
    : workItemTitle(item);
  const patchTitle = workItemPatchTitle(item);
  const entityId =
    !patchTitle && item.targetType === 'entity' && item.targetId && /^\d+$/.test(item.targetId)
      ? Number(item.targetId)
      : null;
  const resolved = useEntityTitle(entityId == null ? undefined : item.courseKey, entityId);

  const title = patchTitle ? (
    <>{patchTitle}</>
  ) : entityId == null ? (
    <>{fallback}</>
  ) : resolved ? (
    <>
      {resolved}{' '}
      <span className="font-mono text-[10px] font-normal text-slate-400" title={`Entity #${entityId}`}>
        #{entityId}
      </span>
    </>
  ) : isLessonFill(item) ? (
    <>
      {item.lessonFill?.placeholderTitle || 'Untitled lesson'}{' '}
      <span className="font-mono text-[10px] font-normal text-slate-400">#{entityId}</span>
    </>
  ) : (
    <>#{entityId}</>
  );

  if (newLessonQuestion) {
    return <>{title}<span className="block text-[11px] font-normal">{plainQuestionReference ? 'Question for the new lesson' : <NewLessonQuestionLink item={item} />}</span></>;
  }

  // Driven by the persisted attachment state only — never inferred from patch entities.
  if (item.targetType === 'task' && practiceItemAttachment(item) === 'unattached') {
    const intendedLesson = item.intendedParent?.nodePath ?? item.intendedParent?.lessonSlug;
    return (
      <>
        {title}
        <span className="block text-[11px] font-normal text-amber-700">
          Intended lesson (pending attach): {intendedLesson}
        </span>
      </>
    );
  }

  return title;
}
